import React from "react";
import {
  FiCode,
  FiLayout,
  FiDatabase,
  FiGitBranch,
  FiCloud,
  FiPieChart,
} from "react-icons/fi";
import {
  FaReact,
  FaFigma,
  FaNodeJs,
  FaPython,
  FaMagnet,
  FaJava,
} from "react-icons/fa";
import { SiTypescript, SiMongodb } from "react-icons/si";
import "./About.css";

const About = () => {
  const skills = [
    { icon: <FaReact />, name: "React.js", category: "Frontend" },
    { icon: <SiTypescript />, name: "TypeScript", category: "Language" },
    { icon: <FaNodeJs />, name: "Node.js", category: "Backend" },
    { icon: <FaFigma />, name: "Figma", category: "Design" },
    { icon: <SiMongodb />, name: "MongoDB", category: "Database" },
    { icon: <FaPython />, name: "Python", category: "Language" },
    { icon: <FaMagnet />, name: "AI/ML", category: "Machine Learning" },
    { icon: <FaJava />, name: "Java", category: "Language" },
  ];

  const services = [
    {
      icon: <FiLayout />,
      title: "UI/UX Design",
      description:
        "Creating intuitive and visually stunning user interfaces that delight users.",
    },
    {
      icon: <FiCode />,
      title: "Web Development",
      description:
        "Building responsive and performant web applications using modern technologies.",
    },
    {
      icon: <FiDatabase />,
      title: "Backend Development",
      description: "Architecting robust and scalable server-side solutions.",
    },
    {
      icon: <FiGitBranch />,
      title: "Version Control",
      description: "Managing code with Git and implementing CI/CD pipelines.",
    },
    {
      icon: <FiCloud />,
      title: "Cloud Services",
      description: "Deploying and managing applications on cloud platforms.",
    },
    {
      icon: <FiPieChart />,
      title: "Machine Learning",
      description: "Building intelligent systems and modern web experiences.",
    },
  ];

  const stats = [
    { number: "10+", label: "Projects Completed" },
    { number: "6+", label: "Happy Clients" },
    { number: "10+", label: "Awards Won" },
    { number: "5+", label: "Github Contributions" },
  ];

  return (
    <section id="about" className="about section">
      <div className="container">
        <div className="section-header">
          <span className="section-subtitle">About Me</span>
          <h2 className="section-title">Debugging life one line at a time.</h2>
          <p className="section-description">
            Passionate about creating beautiful and functional digital products
          </p>
        </div>

        <div className="about-content">
          <div className="about-image fade-in">
            <div className="about-image-wrapper">
              <img src="/assets/about-image.jpg" alt="Divyansh working" />
            </div>
          </div>

          <div className="about-info fade-in delay-2">
            <h3>Hello! I'm Divyansh, a passionate developer.</h3>

            <p>
              I am a developer with a passion for creating meaningful technology
              that solves real-world problems. I enjoy working across the stack,
              turning ideas into reliable and engaging applications. My approach
              combines structured thinking, attention to detail, and a constant
              pursuit of improvement.
            </p>

            <p>
              I am always exploring new tools and technologies to expand my
              skill set and build better solutions. For me, development is not
              just about writing code it’s about crafting experiences that make
              a difference.
            </p>

            <div className="about-highlights">
              <div className="highlight-item">
                <span className="highlight-icon">🎓</span>
                <span>
                  B.Tech in Computer Science, Madan Mohan Malaviya University of
                  Technology
                </span>
              </div>
            </div>

            <div className="about-personal">
              <h4>When I'm not coding, you'll find me:</h4>
              <div className="personal-interests">
                <span className="interest-tag">📚 Reading Books</span>
                <span className="interest-tag">🎨 Sketching</span>
                <span className="interest-tag">🏃‍♀️ Running</span>
                <span className="interest-tag">✈️ Traveling</span>
                <span className="interest-tag">📷 Photography</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="about-stats">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="stat-item fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Services Section */}
        <div className="about-services">
          <h3 className="services-title">What I Do</h3>
          <div className="services-grid">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="service-icon">{service.icon}</div>
                <h4>{service.title}</h4>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Section */}
        <div className="about-skills">
          <h3 className="skills-title">Tools & Technologies</h3>
          <div className="skills-grid">
            {skills.map((skill, index) => (
              <div
                key={index}
                className="skill-item fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="skill-icon">{skill.icon}</div>
                <span className="skill-name">{skill.name}</span>
                <span className="skill-category">{skill.category}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
